import { useEffect, useRef, useState } from 'react';
import { Modal, Button, useToast } from './primitives';
import { SignaturePad } from './SignaturePad';
import {
  usePodpisy,
  useCreatePodpis,
  useSetDefaultPodpis,
  useDeletePodpis,
} from '../api/hooks';
import type { Podpis } from '../api/schemas';
import { cleanSignature, dataUrlToCanvas, fileToCanvas } from '../lib/signatureImage';
import styles from './SignatureLibrary.module.css';

const MAX_FILE_BYTES = 15 * 1024 * 1024;

export interface SignatureLibraryModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Režim výběru: klik na podpis ho vrátí a modal se zavře.
   * Bez `onPick` jde jen o správu knihovny.
   */
  onPick?: (podpis: Podpis) => void;
}

/**
 * Knihovna podpisů — uložené podpisy uživatele (sken z JPG/PNG/PDF nebo nakreslené).
 * Pozadí skenu se odstraní a podpis se ořízne, takže se dá položit do dokumentu
 * přes čáru „podpis" bez bílého obdélníku.
 */
export function SignatureLibraryModal({ open, onClose, onPick }: SignatureLibraryModalProps) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const { data: podpisy = [], isLoading, isError } = usePodpisy(open);
  const createPodpis = useCreatePodpis();
  const setDefault = useSetDefaultPodpis();
  const deletePodpis = useDeletePodpis();

  // Rozpracovaný nový podpis (náhled před uložením).
  const [mode, setMode] = useState<'none' | 'draw' | 'preview'>('none');
  const [drawn, setDrawn] = useState('');
  const [draft, setDraft] = useState('');
  const [draftName, setDraftName] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode('none');
      setDrawn('');
      setDraft('');
      setDraftName('');
    }
  }, [open]);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      toast.error('Soubor je příliš velký (max 15 MB).');
      return;
    }
    setProcessing(true);
    try {
      const canvas = await fileToCanvas(file);
      const png = cleanSignature(canvas, { removeBackground: true });
      if (!png) {
        toast.error('V souboru jsem nenašel žádný podpis (je stránka prázdná?).');
        return;
      }
      setDraft(png);
      setDraftName(file.name.replace(/\.[^.]+$/, '') || 'Podpis');
      setMode('preview');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Soubor se nepodařilo načíst.');
    } finally {
      setProcessing(false);
    }
  }

  async function confirmDrawn() {
    if (!drawn) return;
    const png = cleanSignature(await dataUrlToCanvas(drawn), { removeBackground: false });
    if (!png) return;
    setDraft(png);
    setDraftName('Podpis');
    setMode('preview');
  }

  async function saveDraft() {
    try {
      const created = await createPodpis.mutateAsync({ name: draftName.trim() || 'Podpis', data_url: draft });
      toast.success('Podpis uložen do knihovny.');
      setMode('none');
      setDraft('');
      setDrawn('');
      if (onPick) {
        onPick(created);
        onClose();
      }
    } catch {
      toast.error('Uložení podpisu selhalo.');
    }
  }

  async function handleDefault(id: number) {
    try {
      await setDefault.mutateAsync(id);
    } catch {
      toast.error('Nastavení výchozího podpisu selhalo.');
    }
  }

  async function handleDelete(p: Podpis) {
    if (!window.confirm(`Smazat podpis „${p.name}" z knihovny?`)) return;
    try {
      await deletePodpis.mutateAsync(p.id);
      toast.success('Podpis smazán.');
    } catch {
      toast.error('Smazání podpisu selhalo.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={onPick ? '✍️ Vložit uložený podpis' : '✍️ Knihovna podpisů'}
      size="md"
    >
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,application/pdf,.pdf"
        hidden
        onChange={handleFile}
      />

      {mode === 'none' && (
        <div className={styles.addRow}>
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => fileRef.current?.click()}
            disabled={processing}
          >
            <span className={styles.addIcon} aria-hidden>📎</span>
            <span className={styles.addTitle}>{processing ? 'Zpracovávám…' : 'Nahrát podpis'}</span>
            <span className={styles.addHint}>JPG, PNG nebo PDF (sken). Bílé pozadí se odstraní.</span>
          </button>
          <button type="button" className={styles.addBtn} onClick={() => setMode('draw')}>
            <span className={styles.addIcon} aria-hidden>✍️</span>
            <span className={styles.addTitle}>Nakreslit podpis</span>
            <span className={styles.addHint}>Prstem, stylusem nebo myší.</span>
          </button>
        </div>
      )}

      {mode === 'draw' && (
        <div className={styles.panel}>
          <SignaturePad value={drawn} onChange={setDrawn} width={800} height={220} hideLibrary />
          <div className={styles.panelActions}>
            <Button variant="ghost" size="sm" onClick={() => setMode('none')}>
              Zrušit
            </Button>
            <Button variant="primary" size="sm" onClick={confirmDrawn} disabled={!drawn}>
              Pokračovat
            </Button>
          </div>
        </div>
      )}

      {mode === 'preview' && (
        <div className={styles.panel}>
          <div className={styles.preview}>
            <img src={draft} alt="Náhled podpisu" />
          </div>
          <label className={styles.nameLabel}>
            Název
            <input
              className={styles.nameInput}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              placeholder="např. David Harant – modrý"
            />
          </label>
          <div className={styles.panelActions}>
            <Button variant="ghost" size="sm" onClick={() => setMode('none')}>
              Zahodit
            </Button>
            <Button variant="primary" size="sm" onClick={saveDraft} disabled={createPodpis.isPending}>
              {createPodpis.isPending ? 'Ukládám…' : onPick ? 'Uložit a vložit' : 'Uložit do knihovny'}
            </Button>
          </div>
        </div>
      )}

      <div className={styles.listHead}>Uložené podpisy</div>
      {isLoading ? (
        <div className={styles.empty}>Načítám…</div>
      ) : isError ? (
        <div className={styles.error}>Knihovnu podpisů se nepodařilo načíst.</div>
      ) : podpisy.length === 0 ? (
        <div className={styles.empty}>Zatím žádný uložený podpis.</div>
      ) : (
        <ul className={styles.list}>
          {podpisy.map((p) => (
            <li key={p.id} className={styles.item}>
              <button
                type="button"
                className={styles.thumb}
                onClick={onPick ? () => { onPick(p); onClose(); } : undefined}
                disabled={!onPick}
                title={onPick ? 'Vložit tento podpis' : undefined}
              >
                <img src={p.data_url} alt={p.name} />
              </button>
              <div className={styles.meta}>
                <span className={styles.name}>{p.name}</span>
                {p.is_default && <span className={styles.defaultBadge}>● výchozí</span>}
              </div>
              <div className={styles.actions}>
                {onPick && (
                  <Button size="sm" variant="primary" onClick={() => { onPick(p); onClose(); }}>
                    Vložit
                  </Button>
                )}
                {!p.is_default && (
                  <Button size="sm" variant="secondary" onClick={() => handleDefault(p.id)} title="Nastavit jako výchozí">
                    Výchozí
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => handleDelete(p)} title="Smazat podpis">
                  ✕
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
